/* eslint-disable max-lines */
import React, { ReactNode } from 'react'
import { format, isValid, parse } from 'date-fns'

import Button from 'common/components/button'
import CascadeSelect from 'common/components/cascadeSelect/CascadeSelect'
import DatePicker from 'common/components/datePicker'
import FileUpload from 'common/components/fileUpload/FileUpload'
import FormItem from 'common/components/formItem'
import Input from 'common/components/input'
import RadioGroup from 'common/components/radioGroup'
import SingleSelect from 'common/components/singleSelect'
import Tags from 'common/components/tags'
import TimePickerComponent from 'common/components/timePicker'
import { isYesterdayOrBefore } from 'common/helpers/date'
import { stripForbiddenCharacters } from 'common/helpers/stripForbiddenCharacters'
import { Time as TimeIcon } from 'common/icons'

import GuidingText from '../../GuidingText'

import DaysToGo from './DaysToGo/Daystogo'
import SelectList from './SelectList/Selectlist'
import SelectRadio from './SelectRadio/Selectradio'
import ValidationError from './ValidationError/Validationerror'
import YesNo from './YesNo/Yesno'
import SingleSelectRadioCheckbox from './singleSelectRadioCheckbox'

import styles from './Questions.module.css'

const REACT_APP_DEFAULT_DATE_FORMAT = process.env.REACT_APP_DEFAULT_DATE_FORMAT
const REACT_APP_DEFAULT_DATE_TIME_FORMAT =
  process.env.REACT_APP_DEFAULT_DATE_TIME_FORMAT

if (REACT_APP_DEFAULT_DATE_FORMAT === undefined) {
  throw new Error('REACT_APP_DEFAULT_DATE_FORMAT is required')
}

const toDateFnsFormat = (fmt: string | undefined) =>
  (fmt ?? 'dd MMM yyyy')
    .replace(/YYYY/g, 'yyyy')
    .replace(/DD/g, 'dd')
    .replace(/\bD\b/g, 'd')

export const CalendarIcon = () => (
  <i
    aria-label="icon: calendar"
    className="anticon anticon-calendar ant-calendar-picker-icon"
  >
    <svg
      width="24px"
      height="24px"
      viewBox="0 0 24 24"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Icon/Calendar/Default</title>
      <g
        id="Icon/Calendar/Default"
        stroke="none"
        stroke-width="1"
        fill="none"
        fill-rule="evenodd"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <g
          id="calendar"
          transform="translate(1.000000, 1.000000)"
          stroke="#1F2937"
          stroke-width="1.5"
        >
          <rect
            id="Rectangle"
            x="0"
            y="2.93333333"
            width="22"
            height="19.0666667"
            rx="1.5"
          ></rect>
          <line x1="0" y1="8.8" x2="22" y2="8.8" id="Path"></line>
          <line
            x1="5.86666667"
            y1="5.13333333"
            x2="5.86666667"
            y2="0"
            id="Path"
          ></line>
          <line
            x1="16.1333333"
            y1="5.13333333"
            x2="16.1333333"
            y2="0"
            id="Path"
          ></line>
        </g>
      </g>
    </svg>
  </i>
)

export const getCascaderOptions = (data: any) => {
  // Iterate recursively over the cascader options, and return a mapping from their value to their name
  // This will be required later on when generating
  const valueNameMapping: any = {}
  const getValueNameMapping = (optionList: any) => {
    if (!Array.isArray(optionList)) {
      return
    }
    optionList.forEach((option) => {
      valueNameMapping[option?.value] = option?.name
      if (option?.children) {
        getValueNameMapping(option.children)
      }
    })
  }
  getValueNameMapping(data.option_list)
  return valueNameMapping
}

type Props = {
  data: any
  validationError: string
  value: any
  conditionalHiddenStatus?: boolean
  conditionalParentOpenStatus: boolean
  onChange: Function
  daysToGoDate?: any
  isUploading?: boolean
  token?: string
  campaignId?: string
  uploadManagedCampaignFile?: Function
  removeManagedCampaignFile?: Function
  hideGuidingText?: boolean
  autoFocus?: boolean
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  disabled: boolean
}
class Questions extends React.Component<Props, any> {
  constructor(props: Props, context: any) {
    super(props, context)
    this.state = {
      fileList: [],
      tags:
        props.value && typeof props.value === 'string'
          ? props.value.split(';')
          : [],
    }
  }

  static defaultProps = {
    conditionalHiddenStatus: true,
  }

  disabledDateTime = (current: Date | null, allowPast: any): boolean => {
    if (allowPast || current === null) {
      return false
    }
    return isYesterdayOrBefore(current)
  }

  addCascaderValueIfMissing = (optionList: any) => {
    // Iterate recursively through cascader options, and add a value if it's missing
    if (!Array.isArray(optionList)) {
      return
    }
    return optionList.map((option) => {
      const newOption = option
      if (option?.children) {
        newOption.children = this.addCascaderValueIfMissing(option.children)
      }
      if (!newOption?.value && newOption?.label) {
        return { ...newOption, value: newOption.label }
      }
      return newOption
    })
  }

  // Update the uploadList display under the upload button.
  // Take 2 parameter but the second one is optional, if it is not passed we only update the status.
  updateList(status: any, file: any = undefined) {
    const {
      state,
      state: { fileList },
    } = this
    if (file) {
      this.setState({
        ...state,
        fileList: [
          {
            ...file,
            status,
          },
        ],
      })
    } else {
      const updatedFileList = fileList[0]
      updatedFileList.status = status
      this.setState({
        ...state,
        fileList: [updatedFileList],
      })
    }
  }

  resetList() {
    const { state } = this
    this.setState({
      ...state,
      fileList: [],
    })
  }

  datePickerChecker = (dateInput: Date | null, fmt: string): string => {
    if (dateInput && isValid(dateInput)) {
      return format(dateInput, toDateFnsFormat(fmt))
    }
    return ''
  }

  render() {
    const {
      data,
      validationError,
      onChange,
      value,
      daysToGoDate,
      conditionalHiddenStatus,
      conditionalParentOpenStatus,
      isUploading,
      uploadManagedCampaignFile,
      removeManagedCampaignFile,
      hideGuidingText,
      autoFocus,
      onFocus,
      onBlur,
      disabled,
    } = this.props
    const defaultValue = value
    const { fileList, tags } = this.state
    let input: ReactNode = null

    if (!REACT_APP_DEFAULT_DATE_TIME_FORMAT) {
      throw new Error('REACT_APP_DEFAULT_DATE_TIME_FORMAT is required')
    }

    const timeFormat = 'HH:mm'
    switch (data.type) {
      case 'text_number':
      case 'text_percentage':
      case 'text_currency':
      case 'text_integer':
      case 'text_short':
      case 'text_long':
        input = (
          <Input
            data-cy={data.type}
            data-testid={data.type}
            type="text"
            className="text-base max-w-112"
            variant="default"
            name={data.name}
            value={defaultValue || ''}
            placeholder={data.placeholder}
            onChange={(e) => {
              onChange(
                data.name,
                data.type,
                stripForbiddenCharacters(e.target.value),
                data.required
              )
            }}
            onFocus={onFocus}
            onBlur={onBlur}
            autoFocus={autoFocus}
            disabled={disabled}
          />
        )
        break

      case 'text_list':
        input = (
          <Tags<string>
            isFreeText
            data-cy={data.type}
            data-testid={data.type}
            onRemoveClick={(value) => {
              const updatedTags = tags.filter((tag) => tag !== value)
              onChange(
                data.name,
                data.type,
                updatedTags.join(';'),
                data.required
              )
              this.setState({ tags: updatedTags })
            }}
            onAddClick={(value) => {
              const updatedTags = [...tags, value]
              onChange(
                data.name,
                data.type,
                updatedTags.join(';'),
                data.required
              )
              this.setState({ tags: updatedTags })
            }}
            tags={tags.map((value) => ({ value, label: value }))}
            suggestions={data.suggested_words}
            maxLength={20}
            onInputChange={stripForbiddenCharacters}
            isDisabled={disabled}
          />
        )
        break
      case 'upload':
        input = (
          <FileUpload
            data-cy={data.type}
            accept=".jpg,.jpeg,.pdf,.gif,.png"
            data-testid="file-upload"
            disabled={isUploading}
            customRequest={(opts) => {
              uploadManagedCampaignFile?.(opts.file, this.updateList.bind(this))
            }}
            onChange={(e) => {
              if (e.file.status === 'removed') {
                return
              }
              this.updateList('uploading', e.file)
              onChange(data.name, data.type, e.file.name, data.required)
            }}
            onRemove={() => {
              this.resetList()
              onChange(data.name, data.type, null, data.required)
              removeManagedCampaignFile?.()
            }}
            multiple={false}
            fileList={fileList}
            showUploadList={{
              showDownloadIcon: false,
              showRemoveIcon: true,
            }}
          >
            <Button disabled={disabled}>Click to Upload</Button>
          </FileUpload>
        )
        break
      case 'date_time':
        const dateTimeParsed =
          defaultValue !== '' &&
          defaultValue !== undefined &&
          REACT_APP_DEFAULT_DATE_TIME_FORMAT
            ? parse(
                defaultValue,
                toDateFnsFormat(REACT_APP_DEFAULT_DATE_TIME_FORMAT),
                new Date()
              )
            : null
        const dateTime: Date | null =
          dateTimeParsed && isValid(dateTimeParsed) ? dateTimeParsed : null
        input = (
          <>
            <div className="flex gap-4">
              <DatePicker
                data-cy={data.type}
                data-testid={data.type}
                date={dateTime}
                id="date-time-picker"
                numberOfMonths={1}
                onDateChange={(currentDate) => {
                  const transformedDateString = this.datePickerChecker(
                    currentDate,
                    REACT_APP_DEFAULT_DATE_TIME_FORMAT
                  )
                  onChange(
                    data.name,
                    data.type,
                    transformedDateString,
                    data.required
                  )
                }}
                showClearDate
                disabled={disabled}
                isOutsideRange={(date) =>
                  this.disabledDateTime(date, data.allow_past)
                }
                isTodayVisible={true}
              />
              <TimePickerComponent
                value={dateTime ? format(dateTime, 'HH:mm') : null}
                allowClear={false}
                format={timeFormat}
                style={{ width: '100%' }}
                disabled={disabled}
                className={styles.timePicker}
                onChange={(timeString: string | null) => {
                  if (!timeString) {
                    onChange(data.name, data.type, '', data.required)
                    return
                  }
                  const [h, m] = timeString.split(':').map(Number)
                  const combined = dateTime ? new Date(dateTime) : new Date()
                  combined.setHours(h, m, 0, 0)
                  const transformedDateString = this.datePickerChecker(
                    combined,
                    REACT_APP_DEFAULT_DATE_TIME_FORMAT
                  )
                  onChange(
                    data.name,
                    data.type,
                    transformedDateString,
                    data.required
                  )
                }}
                suffixIcon={<TimeIcon size={6} />}
              />
            </div>
          </>
        )
        break
      case 'date':
        const date1Parsed =
          defaultValue !== '' && defaultValue !== undefined
            ? parse(defaultValue, 'dd-MM-yyyy', new Date())
            : null
        const date1: Date | null =
          date1Parsed && isValid(date1Parsed) ? date1Parsed : null
        input = (
          <>
            <DatePicker
              data-cy={data.type}
              data-testid={data.type}
              date={date1}
              id={data.name}
              numberOfMonths={1}
              onDateChange={(currentDate) => {
                const transformedDateString = this.datePickerChecker(
                  currentDate,
                  'DD-MM-YYYY'
                )
                onChange(
                  data.name,
                  data.type,
                  transformedDateString,
                  data.required
                )
              }}
              showClearDate
              disabled={disabled}
              isOutsideRange={(date) =>
                this.disabledDateTime(date, data.allow_past)
              }
              isTodayVisible={true}
            />
            <GuidingText text={data.tooltip} />
          </>
        )
        break

      case 'daystogo':
        let date: Date | null = null
        if (defaultValue !== '' && defaultValue !== undefined) {
          const dtParsed = parse(
            defaultValue.split('||')[0],
            'dd-MM-yyyy',
            new Date()
          )
          date = isValid(dtParsed) ? dtParsed : null
        }
        input = (
          <DaysToGo
            data-cy={data.type}
            data-testid={data.type}
            value={date}
            defaultValue={defaultValue}
            format={REACT_APP_DEFAULT_DATE_FORMAT ?? ''}
            name={data.name}
            daysToGoDate={daysToGoDate}
            type={data.type}
            required={data.required || false}
            onChange={onChange}
            disabled={disabled}
          />
        )
        break

      case 'select_single': {
        const optionsList: any = []
        data.option_list.forEach((option: any, index: any) => {
          if (index === 0) {
            const curOption: any = {}
            curOption.value = ''
            curOption.label = data.include_no ? 'No' : 'Select an option'
            optionsList.push(curOption)
          }
          const curOption: any = {}
          const optionVal = option[0]
          curOption.label = optionVal
          curOption.value =
            typeof option[1] !== 'undefined' ? option[1] : option[0]
          optionsList.push(curOption)
        })
        input = (
          <SingleSelect
            data-cy={data.type}
            data-testid={data.type}
            value={defaultValue}
            onChange={(val: any) => {
              onChange(data.name, data.type, val?.value, data.required)
            }}
            isSearchable
            isDisabled={disabled}
            menuPortalTarget={document.body}
            menuShouldBlockScroll={true}
            options={optionsList.map((option: any) => ({
              label: option.label,
              value: option.value,
            }))}
          />
        )
        break
      }

      case 'select_single_radio':
        input = ''
        break
      case 'select_single_checkbox':
        input = (
          <SingleSelectRadioCheckbox
            data={data}
            defaultValue={defaultValue}
            disabled={disabled}
            onChange={onChange}
          />
        )
        break

      case 'yes_no':
        input = (
          <>
            <YesNo
              defaultValue={defaultValue}
              name={data.name}
              type={data.type}
              onChange={onChange}
              disabled={disabled}
            />
          </>
        )
        break

      case 'select_list':
        input = (
          <SelectList
            data-cy={data.type}
            data-testid={data.type}
            defaultValue={defaultValue}
            list={data.word_list}
            max_limit={data.max_limit}
            name={data.name}
            type={data.type}
            required={data.required}
            onChange={onChange}
            disabled={disabled}
          />
        )
        break

      case 'select_radio':
      case 'select_radio_integer':
      case 'select_radio_percentage':
      case 'select_radio_currency':
        input = (
          <>
            <SelectRadio
              data-cy={data.type}
              data-testid={data.type}
              defaultValue={defaultValue}
              name={data.name}
              type={data.type}
              onChange={onChange}
              placeholder={data.placeholder || ''}
              disabled={disabled}
            />
            {hideGuidingText ? null : <GuidingText text={data.tooltip} />}
          </>
        )
        break
      case 'cascader':
        data.option_list = this.addCascaderValueIfMissing(data.option_list)
        const valueNameMapping = getCascaderOptions(data)
        input = (
          <CascadeSelect
            name={data.name}
            options={data.option_list}
            onChange={(val) => {
              onChange(data.name, data.type, val, data.required, {
                valueNameMapping,
              })
            }}
            placeholder={data.placeholder || 'Please select'}
            defaultValue={[defaultValue]}
            value={defaultValue}
            disabled={disabled}
          />
        )
        break
      case 'radio_button':
        input = (
          <>
            <RadioGroup
              data-cy={data.type}
              data-testid={data.type}
              name={data.name}
              onChange={(newValue) =>
                onChange(data.name, data.type, newValue, data.required)
              }
              value={defaultValue}
              options={data.option_list}
              isDisabled={disabled}
              variant="withBackground"
            />
            {hideGuidingText ? null : <GuidingText text={data.tooltip} />}
          </>
        )
        break
      default:
        input = ''
        break
    }
    let classNames = `question-items ${data.type} m-b--20`
    classNames += data.required ? ' required' : ''
    classNames += validationError ? ' error' : ''
    classNames += conditionalHiddenStatus === false ? ' conditional-child' : ''
    classNames +=
      conditionalParentOpenStatus === true ? ' conditional-parent' : ''
    const selectRadioTypes = [
      'select_radio',
      'select_radio_integer',
      'select_radio_currency',
      'select_radio_percentage',
      'radio_button',
    ]

    const label = data.text ? (
      data.required ? (
        data.text
      ) : (
        <React.Fragment>
          {data.text}
          <span className="text-coolGray-400"> (optional)</span>
        </React.Fragment>
      )
    ) : null

    const tooltip = data.tooltip ? (
      data.required || data.text ? (
        data.tooltip
      ) : (
        <React.Fragment>
          <span className="text-coolGray-400">(Optional) </span>
          {data.tooltip}
        </React.Fragment>
      )
    ) : null

    if (selectRadioTypes.includes(data.type)) {
      return (
        <FormItem label={label} className={classNames}>
          {input}
          <ValidationError message={validationError} />
        </FormItem>
      )
    }
    return (
      <FormItem label={label} className={classNames}>
        {data.type === 'date' || data.type === 'daystogo' ? (
          <>
            {input}
            {hideGuidingText ? null : (
              <ValidationError message={validationError} />
            )}
          </>
        ) : (
          <>
            {input}
            {hideGuidingText ? null : (
              <>
                <ValidationError message={validationError} />
                <GuidingText text={tooltip} />
              </>
            )}
          </>
        )}
      </FormItem>
    )
  }
}

export default Questions
